<template>
  <section class="form-section">
    <div class="section-header">
      <h2 class="section-title">Extracurricular Activities</h2>
      <ContextMenu
        :is-authenticated="isAuthenticated"
        add-new-label="Add New Activity"
        add-from-master-label="Add from Profile"
        @add-new="addActivity"
        @add-from-master="handleAddFromMaster"
      />
    </div>
    
    <div v-for="(activity, index) in activitiesList" :key="index" class="form-card">
      <div class="card-header">
        <h3 class="card-title">Activity {{ index + 1 }}</h3>
        <div class="flex items-center space-x-1 sm:space-x-2">
          <button
            v-if="isAuthenticated && isEntrySyncable(activity)"
            type="button"
            @click.stop="$emit('sync-entry', { entry: activity, entryType: 'activity' })"
            class="flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#000537]/25 text-[#000537] dark:border-indigo-400/40 dark:text-indigo-400 text-[10px] font-medium hover:bg-[#000537]/5 dark:hover:bg-indigo-400/10 transition-colors"
          >
            <svg class="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
            Save to profile
          </button>
          <button
            type="button"
            @click="moveActivityUp(index)"
            class="btn-reorder"
            v-if="index > 0"
            title="Move up"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
            </svg>
          </button>
          <button
            type="button"
            @click="moveActivityDown(index)"
            class="btn-reorder"
            v-if="index < activitiesList.length - 1"
            title="Move down"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <button type="button" @click="removeActivity(index)" class="btn-remove" v-if="activitiesList.length > 1" title="Delete">
            <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">Role/Position</label>
          <input 
            type="text" 
            :value="activity.role"
            @input="updateActivityField(index, 'role', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'role') }]"
            placeholder="Vice President"
            required
          />
          <div v-if="isFieldHighlighted(index, 'role')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Organization</label>
          <input 
            type="text" 
            :value="activity.organization"
            @input="updateActivityField(index, 'organization', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'organization') }]"
            placeholder="Student Government Association"
            required
          />
          <div v-if="isFieldHighlighted(index, 'organization')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        
        <div class="form-group">
          <label class="form-label">Start Date</label>
          <div class="flex space-x-1 sm:space-x-2">
            <select
              :value="formatMonth(activity.startMonth)"
              @change="updateActivityField(index, 'startMonth', $event.target.value)"
              class="form-input flex-1"
              required
            >
              <option value="">Month</option>
              <option value="01">Jan</option>
              <option value="02">Feb</option>
              <option value="03">Mar</option>
              <option value="04">Apr</option>
              <option value="05">May</option>
              <option value="06">Jun</option>
              <option value="07">Jul</option>
              <option value="08">Aug</option>
              <option value="09">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
            <input 
              type="number" 
              :value="activity.startYear" 
              @input="updateActivityField(index, 'startYear', $event.target.value)"
              class="form-input flex-1" 
              placeholder="2024"
              min="1950" 
              max="2030"
              required
            />
          </div>
        </div>
        <div class="form-group">
          <div class="flex items-start sm:items-center justify-between mb-1 flex-col sm:flex-row space-y-1 sm:space-y-0">
            <label class="form-label mb-0">End Date</label>
            <div class="flex items-center">
              <input 
                type="checkbox" 
                :id="`activity-current-${index}`" 
                :checked="activity.isCurrent"
                @change="updateActivityField(index, 'isCurrent', $event.target.checked)"
                class="currently-checkbox"
              />
              <label :for="`activity-current-${index}`" class="currently-label">
                Currently active
              </label>
            </div>
          </div>
          <div v-if="!activity.isCurrent" class="flex space-x-1 sm:space-x-2">
            <select
              :value="formatMonth(activity.endMonth)"
              @change="updateActivityField(index, 'endMonth', $event.target.value)"
              class="form-input flex-1"
              required
            >
              <option value="">Month</option>
              <option value="01">Jan</option>
              <option value="02">Feb</option>
              <option value="03">Mar</option>
              <option value="04">Apr</option>
              <option value="05">May</option>
              <option value="06">Jun</option>
              <option value="07">Jul</option>
              <option value="08">Aug</option>
              <option value="09">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
            <input 
              type="number" 
              :value="activity.endYear" 
              @input="updateActivityField(index, 'endYear', $event.target.value)"
              class="form-input flex-1" 
              placeholder="2024"
              min="1950" 
              max="2030"
              required
            />
          </div>
        </div>
        <div class="form-group col-span-full">
          <div class="flex items-center justify-between">
            <label class="form-label">Responsibilities & Achievements</label>
            <BulletOptimizer
              v-if="isAuthenticated"
              entry-type="activity"
              :bullets="activity.bulletPoints"
              :context="{
                organization: activity.organization,
                role: activity.role
              }"
              :cv-entry-id="activity.id || null"
              :master-entry-id="activity.masterActivityId || null"
              @apply="applyOptimizedBullets(index, $event)"
            />
          </div>
          <div class="space-y-1">
            <div
              v-for="(bulletPoint, bulletIndex) in activity.bulletPoints"
              :key="`activity-${index}-bullet-${bulletIndex}`"
              class="flex items-start space-x-1.5"
            >
              <span class="text-gray-400 dark:text-gray-500 text-xs mt-2 flex-shrink-0">•</span>
              <input
                type="text"
                :value="bulletPoint"
                @input="handleBulletInput(index, bulletIndex, $event.target.value)"
                :class="['form-input flex-1 min-w-0', { 'bullet-too-long': isBulletTooLong(bulletPoint) }]"
                :placeholder="bulletIndex === 0 ? 'Led team of 20 students in organizing campus-wide events' : 'Add another responsibility or achievement...'"
              />
              <span
                v-if="isBulletTooLong(bulletPoint)"
                class="bullet-length-hint mt-2 flex-shrink-0"
                title="Longer bullets may wrap to a second line in the PDF"
              >{{ bulletPoint.trim().length }}/{{ maxBulletChars }}</span>
              <button
                v-if="activity.bulletPoints.length > 1 && bulletPoint.trim() !== ''"
                type="button"
                @click="removeBullet(index, bulletIndex)"
                class="text-gray-400 hover:text-red-500 transition-colors mt-2 flex-shrink-0"
                title="Remove bullet point"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Reference Documents Section (only for authenticated users) -->
      <ReferenceDocumentSection
        v-if="isAuthenticated"
        :entry-id="activity.id || null"
        entry-type="activity"
        :is-authenticated="isAuthenticated"
        @documents-updated="handleDocumentsUpdated"
      />
    </div>

    <!-- Master Data Modal -->
    <MasterDataModal 
      :is-open="showMasterDataModal"
      data-type="activities"
      @close="closeMasterDataModal"
      @items-selected="handleMasterDataSelection"
    />
  </section>
</template>

<script>
import MasterDataModal from './MasterDataModal.vue'
import ContextMenu from './ContextMenu.vue'
import ReferenceDocumentSection from './ReferenceDocumentSection.vue'
import BulletOptimizer from './BulletOptimizer.vue'
import { CV_CONFIG } from '../config/cvConfig.js'

export default {
  name: 'ActivitiesForm',
  components: {
    MasterDataModal,
    ContextMenu,
    ReferenceDocumentSection,
    BulletOptimizer
  },
  props: {
    activitiesList: {
      type: Array,
      required: true,
      default: () => [{
        organization: '',
        role: '',
        startMonth: '',
        startYear: '',
        endMonth: '',
        endYear: '',
        bulletPoints: [''],
        isCurrent: false
      }]
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    syncableIds: {
      type: Array,
      default: () => []
    },
    highlightedFields: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:activitiesList', 'field-changed', 'scroll-to-new', 'sync-entry'],
  data() {
    return {
      showMasterDataModal: false,
      maxBulletChars: CV_CONFIG.validation.maxBulletChars
    }
  },
  methods: {
    isBulletTooLong(bullet) {
      return (bullet || '').trim().length > this.maxBulletChars
    },

    isEntrySyncable(activity) {
      if (!activity.organization) return false
      if (!activity.id) return true
      return this.syncableIds.includes(activity.id)
    },

    formatMonth(m) {
      if (m === null || m === undefined || m === '') return ''
      return String(m).padStart(2, '0')
    },

    updateActivityField(index, field, value) {
      const updatedList = [...this.activitiesList]
      updatedList[index] = { ...updatedList[index], [field]: value }
      this.$emit('update:activitiesList', updatedList)
      this.$emit('field-changed')
    },

    addActivity() {
      const updatedList = [...this.activitiesList, {
        organization: '',
        role: '',
        startMonth: '',
        startYear: '',
        endMonth: '',
        endYear: '',
        bulletPoints: [''],
        isCurrent: false
      }]
      this.$emit('update:activitiesList', updatedList)
      this.$emit('field-changed')
      
      // Emit scroll event with new entry index
      this.$nextTick(() => {
        this.$emit('scroll-to-new', { type: 'activity', index: updatedList.length - 1 })
      })
    },

    removeActivity(index) {
      if (this.activitiesList.length > 1) {
        const updatedList = [...this.activitiesList]
        updatedList.splice(index, 1)
        this.$emit('update:activitiesList', updatedList)
        this.$emit('field-changed')
      }
    },

    moveActivityUp(index) {
      if (index > 0) {
        const updatedList = [...this.activitiesList]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index - 1, 0, item)
        this.$emit('update:activitiesList', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'activity', index: index - 1 })
        })
      }
    },

    moveActivityDown(index) {
      if (index < this.activitiesList.length - 1) {
        const updatedList = [...this.activitiesList]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index + 1, 0, item)
        this.$emit('update:activitiesList', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'activity', index: index + 1 })
        })
      }
    },

    handleBulletInput(index, bulletIndex, value) {
      const updatedList = [...this.activitiesList]
      const activity = { ...updatedList[index] }
      activity.bulletPoints = [...activity.bulletPoints]
      activity.bulletPoints[bulletIndex] = value
      
      // Add new bullet point if this is the last one and it's not empty
      if (value.trim() !== '' && bulletIndex === activity.bulletPoints.length - 1) {
        activity.bulletPoints.push('')
      }
      
      // Remove empty bullet points (except the last one)
      for (let i = activity.bulletPoints.length - 2; i >= 0; i--) {
        if (activity.bulletPoints[i].trim() === '' && i !== activity.bulletPoints.length - 1) {
          activity.bulletPoints.splice(i, 1)
        }
      }
      
      updatedList[index] = activity
      this.$emit('update:activitiesList', updatedList)
      this.$emit('field-changed')
    },

    removeBullet(index, bulletIndex) {
      const updatedList = [...this.activitiesList]
      const activity = { ...updatedList[index] }

      if (activity.bulletPoints.length > 1) {
        activity.bulletPoints = [...activity.bulletPoints]
        activity.bulletPoints.splice(bulletIndex, 1)
        updatedList[index] = activity
        this.$emit('update:activitiesList', updatedList)
        this.$emit('field-changed')
      }
    },

    applyOptimizedBullets(index, newBullets) {
      const updatedList = [...this.activitiesList]
      const activity = { ...updatedList[index] }
      // Keep the trailing empty bullet so the "add another" input stays available
      activity.bulletPoints = [...newBullets, '']
      updatedList[index] = activity
      this.$emit('update:activitiesList', updatedList)
      this.$emit('field-changed')
    },

    handleAddFromMaster() {
      this.showMasterDataModal = true
    },

    handleMasterDataSelection(data) {
      // Convert master data items to activities format
      const newActivitiesEntries = data.items.map(item => ({
        masterActivityId: item.id || null,
        role: item.role || '',
        organization: item.organization || '',
        startMonth: item.start_month || '',
        startYear: item.start_year || '',
        endMonth: item.end_month || '',
        endYear: item.end_year || '',
        bulletPoints: item.base_bullet_points?.length ? item.base_bullet_points : [''],
        isCurrent: item.is_current || false
      }))

      // Add the new entries to the existing list
      const updatedList = [...this.activitiesList, ...newActivitiesEntries]
      this.$emit('update:activitiesList', updatedList)
      this.$emit('field-changed')

      // Scroll to the first new entry
      if (newActivitiesEntries.length > 0) {
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { 
            type: 'activity', 
            index: this.activitiesList.length 
          })
        })
      }
    },

    closeMasterDataModal() {
      this.showMasterDataModal = false
    },

    isFieldHighlighted(index, fieldName) {
      const highlightedEntry = this.highlightedFields.find(entry => entry.index === index)
      return highlightedEntry && highlightedEntry.fields.includes(fieldName)
    },

    handleDocumentsUpdated(data) {
      // This method handles when reference documents are updated
      console.log(`Activity entry ${data.entryId} now has ${data.documentCount} reference documents`)
      this.$emit('field-changed') // Trigger any auto-save or preview updates
    }
  }
}
</script>

<style scoped>
/* Field Highlighting Styles */
.field-needs-review {
  @apply border-2 border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20;
}

.field-needs-review:focus {
  @apply border-red-600 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-800;
}

.review-badge {
  @apply inline-flex items-center text-xs font-medium text-red-600;
}

/* Subtle warning for bullets that would wrap to a second line in the PDF */
.bullet-too-long {
  @apply border-amber-400 dark:border-amber-500;
}

.bullet-length-hint {
  @apply text-[10px] text-amber-500 dark:text-amber-400 tabular-nums;
}
</style>